import { useMemo } from 'react';
import { COLOR_RATES, DUPLEX_DISCOUNT_RATE, PAPER_SIZES, STAPLE_FEE_PER_SET } from '@/constants/printOptions';
import { pageCountForConfig } from '@/utils/pageRange';
import type { PrintJobConfig, PrintJobCalculation } from '@/types/index';

export interface KioskRates {
  bwRatePerPage: number | null;
  colorRatePerPage: number | null;
  duplexExtraCharge: number | null;
  merchantFees?: number | null;
  convenienceFee?: number | null;
}

export function usePrintCalculator(
  config: PrintJobConfig,
  totalPages: number,
  kioskRates?: KioskRates,
): PrintJobCalculation {
  return useMemo(() => {
    const pageCount = pageCountForConfig(
      config.rangeMode,
      totalPages,
      config.fromPage,
      config.toPage,
      config.rangeExpression,
    );

    const impressions = pageCount * config.copies;
    const physicalSheets = config.isDuplex ? Math.ceil(impressions / 2) : impressions;

    const ratePerPage =
      config.colorMode === 'color'
        ? (kioskRates?.colorRatePerPage ?? COLOR_RATES.color) + (kioskRates?.merchantFees || 0)
        : (kioskRates?.bwRatePerPage ?? COLOR_RATES.bw) + (kioskRates?.merchantFees || 0);

    const paperSurchargePerPage = PAPER_SIZES.find((p) => p.id === config.paperSizeId)?.priceDelta ?? 0;

    const basePrice = impressions * (ratePerPage + paperSurchargePerPage);

    // Kiosk-specific duplex discount rate wins when the kiosk has an
    // active RateCharge; otherwise fall back to the static default.
    // NOTE: field is still named `duplex_extra_charge` on the backend —
    // it's being repurposed as a discount rate here, per plan to rename
    // it later.
    const duplexDiscountRate = kioskRates?.duplexExtraCharge ?? DUPLEX_DISCOUNT_RATE;
    // Duplex only applies to multi-page jobs; the server ignores it for a single page
    const ecoDiscount = config.isDuplex && pageCount > 1 ? basePrice * duplexDiscountRate : 0;

    const stapleFee = config.staple ? config.copies * STAPLE_FEE_PER_SET : 0;

    // Flat fee, charged once per print request (not per page)
    const convenienceFee = kioskRates?.convenienceFee || 0;

    const total = Math.max(0, basePrice - ecoDiscount + stapleFee) + convenienceFee;

    // The merchant commission already inside basePrice (before any duplex rebate), shown as its own line
    const merchantFeeTotal = impressions * (kioskRates?.merchantFees || 0);

    return {
      merchantFeeTotal,
      pageCount,
      impressions,
      physicalSheets,
      basePrice,
      ecoDiscount,
      stapleFee,
      paperSurcharge: paperSurchargePerPage * impressions,
      convenienceFee,
      total,
    };
  }, [config, totalPages, kioskRates]);
}